import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { IRFixing, KeyValue } from '@approot/models';
import { AuthService, MarketsService } from '@approot/services';
import { DateUtilities } from '@approot/utilities';
import dayjs from 'dayjs';
import { BehaviorSubject, Subscription } from 'rxjs';
@Component({
  selector: 'app-ir-fixings',
  templateUrl: './ir-fixings.component.html',
  styleUrls: ['./ir-fixings.component.scss']
})
export class IRFixingsComponent implements OnInit, OnDestroy {
  public fixingForm: FormGroup;
  public selectedDateIndex: number = null; // this holds the index of the date that is currently selected
  public indexes: string[] = ['SOFR', '3m EURIBOR'];
  public areTenorsLoading = false;
  private fixingsArray = new FormArray([], this.uniqueDateValidator);
  private currentSubscription: Subscription;
  @ViewChild('divToScroll', { static: false }) private divToScroll: ElementRef;
  private _fixings = new BehaviorSubject<IRFixing[]>([]);

  constructor(
    private marketsService: MarketsService,
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private authService: AuthService
  ) {}

  public ngOnInit() {
    this.fixingForm = this.fb.group({
      index: this.fb.control('', [Validators.required]),
      fixings: this.fixingsArray
    });

    this.fixingForm.get('index').valueChanges.subscribe(cross => {
      if (cross) {
        if (this.fixingsArray.length) {
          this.fixingsArray.clear();
        }
        this.areTenorsLoading = true;

        this.authService
          .fxPost(
            {
              task: 'getIrFixings',
              index: cross
            },
            'marketDataService',
            ''
          )
          .subscribe({
            next: (res: IRFixing[]) => {
              this.areTenorsLoading = false;
              this._fixings.next(res);
              res.forEach((fixing: IRFixing) => {
                const tempDateGroup = new FormGroup({
                  date: new FormControl(
                    DateUtilities.dateToPrettyDate(fixing.date)
                  ),
                  value: new FormControl(+(+fixing.value).toFixed(6))
                });

                tempDateGroup
                  .get('date')
                  .setValidators([
                    this.dateControlValidator.bind(tempDateGroup.get('date'))
                  ]);

                tempDateGroup
                  .get('value')
                  .setValidators([
                    this.numberControlValidator.bind(
                      tempDateGroup.get('value')
                    ),
                    Validators.required
                  ]);

                this.fixingsArray.push(tempDateGroup);
              });
            }
          });
      }
    });
  }

  public ngOnDestroy(): void {
    clearTimeout(this.marketsService.timer);
    this.marketsService.processingInfo = '';
    this.marketsService.currentAction?.unsubscribe();
    this.currentSubscription?.unsubscribe();
  }

  public get showFixings(): boolean {
    return this.marketsService.showIRFixings;
  }

  public set showFixings(val) {
    this.marketsService.showIRFixings = val;
  }

  public modalChanged(event): void {
    // the timeout may still be running if the user decides to close the screen early, so clear it
    clearTimeout(this.marketsService.timer);
    this.marketsService.processingInfo = '';
    this.marketsService.currentAction?.unsubscribe();
    this.currentSubscription?.unsubscribe();
    this.marketsService.showIRFixings = event;
  }

  public returnDates() {
    return (this.fixingForm.get('fixings') as FormArray).controls;
  }

  public removeFixing(fixingIndex: number): void {
    (this.fixingForm.get('fixings') as FormArray).removeAt(fixingIndex);
  }

  public addNewDate(): void {
    const tempGroup = new FormGroup({
      date: new FormControl(''),
      value: new FormControl(''),
      canBeRemoved: new FormControl(true)
    });

    tempGroup
      .get('date')
      .setValidators([this.dateControlValidator.bind(tempGroup.get('date'))]);

    tempGroup
      .get('value')
      .setValidators([
        this.numberControlValidator.bind(tempGroup.get('value')),
        Validators.required
      ]);

    (this.fixingForm.get('fixings') as FormArray).push(tempGroup);
    setTimeout(() => {
      this.updateScroll();
    }, 0);
    this.changeDetectorRef.detectChanges();
  }

  public updateScroll(): void {
    let e = this.divToScroll.nativeElement;
    e.scrollTop = e.scrollHeight;
  }

  public get processingInfo(): string {
    // hold the status of the information sent to the database
    return this.marketsService.processingInfo;
  }

  public onSaveFixingChanges(): void {
    // we only want to save fixings that have been changed. So we filter the controls
    // and only keep the dirty ones for processing
    const filteredDirty = (
      this.fixingForm.get('fixings') as FormArray
    ).controls.filter((x: AbstractControl) => {
      return x.dirty === true;
    });

    // fixing source and currencypair will be the same for all dirty records
    const _index = this.fixingForm.get('index').value;

    // attach the necessary data, the counter 'i' is used to add a millisecond to each records'
    // timestamp so that the dynamodb record will be unique by userId and timestamp
    let i = 0;
    filteredDirty.map(y => {
      i++;
      (y.value['date'] = DateUtilities.prettyDateToDate(y.value['date'])),
        (y.value['index'] = _index);
    });

    if (filteredDirty.length) {
      this.marketsService.saveIRFixings(
        filteredDirty.map(x => x.value),
        this.fixingForm
      );
    }
  }

  public onDateChange(): void {
    const dateElem = (this.fixingForm.get('fixings') as FormArray).controls[
      this.selectedDateIndex
    ].get('date');

    if (dateElem.valid) {
      const filledDate = this.getDateFromCurrentYear(
        dateElem.value.toLowerCase()
      );
      if (filledDate) {
        dateElem.patchValue(DateUtilities.dateToPrettyDate(filledDate), {
          emitEvent: false
        });
      }
    }
  }

  private uniqueDateValidator(
    formArray: AbstractControl
  ): ValidationErrors | null {
    const dateArray = formArray.value.map((item: any) => item.date);
    const hasDuplicate = dateArray.some(
      (date: string, index: number) => dateArray.indexOf(date) !== index
    );
    return hasDuplicate ? { duplicateDates: true } : null;
  }

  private dateControlValidator(control: FormControl): KeyValue | null {
    if (
      DateUtilities.isCorrectDateFormat(control.value) ||
      DateUtilities.isValidLag(control.value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  private numberControlValidator(control: FormControl): KeyValue | null {
    if (
      !isNaN(control.value) &&
      new RegExp('^-?\\d+\\.?\\d{0,6}$').test(control.value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  private getDateFromCurrentYear(date: string): string {
    // date as dd MMM
    const dateWithYear = dayjs(`${date} ${dayjs().year()}`);
    if (!dateWithYear.isValid()) {
      return 'Invalid date';
    }
    return dateWithYear.format('YYYY-MM-DD');
  }
}
